import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { chatService } from "@/services/chat.service";
import { postsService } from "@/services/posts.service";
import { mapPostToFeed, type FeedPost } from "@/features/stash/feedPost";
import { collectionDisplayTitle, useCollectionCatalog } from "@/hooks/useCollections";
import { useListTags, useTagStats } from "@/hooks/useTagsWithCounts";
import { useIsTabActive } from "@/contexts/TabActivityContext";
import { searchMatches, type ResultFacet } from "./searchModel";

const unique = (ids: string[]) => [...new Set(ids.filter(Boolean))];

/** Stuff matching a query: entity ids, a page at a time (native StuffSearch). */
export function useStuffSearch(query: string, enabled = true) {
  const term = query.trim();
  const result = useInfiniteQuery({
    queryKey: ["search", "stuff", term],
    queryFn: ({ pageParam }) => chatService.searchIds(term, "stuff", pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: last => last.next_cursor ?? undefined,
    enabled: enabled && term.length > 0,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const ids = useMemo(() => unique(result.data?.pages.flatMap(page => page.ids) ?? []), [result.data]);
  const total = result.data?.pages[0]?.total;
  return {
    ...result,
    ids,
    count: total ?? ids.length,
    complete: result.isSuccess && (total !== undefined || !result.hasNextPage),
  };
}

/** Explore: other people's public stashes matching a query, a page at a time (native public search). */
export function useExploreSearch(query: string, enabled = true) {
  const active = useIsTabActive();
  const term = query.trim();
  const result = useInfiniteQuery({
    queryKey: ["search", "public", term],
    // Ids from the search, stashes read by id as every list reads them.
    queryFn: async ({ pageParam }) => {
      const page = await postsService.searchPublic(term, pageParam);
      const { posts } = await postsService.batchGetPosts(page.ids);
      return { ...page, posts: page.ids.flatMap(id => (posts?.[id] ? [mapPostToFeed(posts[id])] : [])) };
    },
    initialPageParam: null as string | null,
    getNextPageParam: last => last.next_cursor ?? undefined,
    enabled: active && enabled && term.length > 0,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const posts = useMemo<FeedPost[]>(() => {
    const seen = new Set<string>();
    return (result.data?.pages ?? []).flatMap(page => page.posts).filter(post => !seen.has(post.id) && !!seen.add(post.id));
  }, [result.data]);
  return { ...result, posts };
}

/**
 * Everything a query finds in the account beside its stashes, with each kind's count and
 * whether that count is final (native SearchView): Stuff from the server, collections and
 * tags matched here against what the account has.
 */
export function useSearchCatalogMatches(query: string) {
  const collectionsQuery = useCollectionCatalog();
  const tagsQuery = useListTags({ includeSuggested: true });
  const catalog = collectionsQuery.data;
  const tagList = tagsQuery.data;
  const collections = useMemo(
    () => (catalog ?? []).filter(c => searchMatches(`${collectionDisplayTitle(c.title)} ${c.description ?? ""}`, query)),
    [catalog, query],
  );
  const tags = useMemo(() => (tagList ?? []).filter(tag => tag.state !== "hidden" && searchMatches(tag.name, query)), [tagList, query]);
  return { collections, tags, collectionsQuery, tagsQuery };
}

export function useSearchKinds(query: string, stashes: { count: number; complete: boolean }) {
  const active = useIsTabActive();
  const stuff = useStuffSearch(query, active);
  const { collections, tags: matchedTags, collectionsQuery, tagsQuery } = useSearchCatalogMatches(query);
  const stats = useTagStats(undefined, { enabled: active });
  const tags = useMemo(() => matchedTags.map(tag => ({ ...tag, count: stats.data?.[tag.id] })), [matchedTags, stats.data]);
  const counts: Record<ResultFacet, number> = {
    stashes: stashes.count,
    stuff: stuff.count,
    collections: collections.length,
    tags: tags.length,
  };
  const complete: Record<ResultFacet, boolean> = {
    stashes: stashes.complete,
    stuff: stuff.complete,
    collections: collectionsQuery.isSuccess,
    tags: tagsQuery.isSuccess,
  };
  return { stuff, collections, tags, counts, complete, collectionsQuery, tagsQuery };
}
